import React from "react";
import { getOptimizedUrl } from "../utils/cloudinaryOptimize";
import { Add, Remove, ShoppingCartOutlined, DeleteOutline } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  decrementCartItem,
  incrementCartItem,
  removeItemFromCart,
} from "../redux/cartRedux";
import { useHistory } from "react-router-dom";
import Layout from "../components/Global/Layout";
import { formatCurrency } from "../config/market";
import useMinimumOrderValue from "../hooks/useMinimumOrderValue";
import {
  linesSubtotal,
  minimumOrderShortfall,
  minimumOrderMessage,
} from "../utils/minimumOrder";
import MinimumOrderNotice from "../components/MinimumOrderNotice";
import "./Cart.css";


const Cart = () => {
  const products = useSelector((state) => state.cart.products);
  /* Summed from the lines, not read off `cart.total`: that field is moved by
     deltas on every add and remove and never recomputed, so it drifts, and the
     minimum-order gate cannot be decided on a number that drifts. The rows
     below already price themselves from the lines, so this also makes the
     summary agree with them. */
  const total = linesSubtotal(products);
  const minimumOrderValue = useMinimumOrderValue();
  const minimumShortfall = minimumOrderShortfall(total, minimumOrderValue);
  const dispatch = useDispatch();
  const history = useHistory();
  const [zoomedImage, setZoomedImage] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 968);

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 968);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle keyboard events for zoom modal (mobile only)
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && zoomedImage) {
        setZoomedImage(null);
      }
    };

    if (isMobile) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [zoomedImage, isMobile]);

  const removeFromCartHandler = (item) => {
    dispatch(removeItemFromCart({ _id: item._id }));
  };

  const incrementCartHandler = (item) => {
    dispatch(incrementCartItem({ item }));
  };
  const decrementCartHandler = (item) => {
    dispatch(decrementCartItem({ _id: item._id }));
  };

  const handleContinueShopping = () => {
    // history.push("/shop");
    history.push("/");
  };

  const checkoutHandler = () => {
    if (minimumShortfall > 0) return;
    history.push("/shipping");
  };
  return (
    <Layout>
      <div className="cart-container">
        {/* Cart Header */}
        <div className="cart-header">
          <div className="cart-header-content">
            <ShoppingCartOutlined className="cart-header-icon" />
            <h1 className="cart-header-title">
              Shopping Cart ({products?.length || 0})
            </h1>
          </div>
          <Button
            className="continue-shopping-btn"
            onClick={handleContinueShopping}
            startIcon={<ShoppingCartOutlined />}
          >
            Continue Shopping
          </Button>
        </div>

        <div className="cart-layout">
          {/* Empty Cart State */}
          {!products?.length > 0 ? (
            <div className="empty-cart-container">
              <div className="empty-cart-content">
                <div className="empty-cart-icon-wrapper">
                  <ShoppingCartOutlined className="empty-cart-icon" />
                </div>
                <h3 className="empty-cart-title">Your Cart is Empty</h3>
                <p className="empty-cart-description">
                  Looks like you haven't added anything to your cart yet. Start shopping to fill it up!
                </p>
                <Button
                  variant="contained"
                  className="start-shopping-btn"
                  onClick={handleContinueShopping}
                  startIcon={<ShoppingCartOutlined />}
                >
                  Start Shopping
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Cart Items Section */}
              <div className="cart-items-section">
                <h2 className="cart-items-title">Cart Items</h2>
                {products.map((item) => (
                  <div key={item._id} className="cart-item">
                    <div
                      className="cart-item-image-wrapper"
                      onClick={() => isMobile && setZoomedImage({ url: item.imageUrl, name: item.name })}
                    >
                      <img
                        src={getOptimizedUrl(item.imageUrl, 'cartItem')}
                        alt={item.name}
                        className="cart-item-image"
                      />
                      {/* Amazon-style zoom preview (desktop only) */}
                      {!isMobile && (
                        <div className="image-zoom-preview">
                          <img src={getOptimizedUrl(item.imageUrl, 'productHero')} alt={item.name} />
                        </div>
                      )}
                    </div>

                    <div className="cart-item-details">
                      <h3 className="cart-item-name">{item.name}</h3>
                      <p className="cart-item-stock">
                        In Stock: <span className="cart-item-stock-value">{item.countInStock}</span>
                      </p>

                      <div className="cart-item-actions">
                        <div className="quantity-controls">
                          <IconButton
                            className="quantity-btn"
                            onClick={() => decrementCartHandler(item)}
                            disabled={item.qty <= 1}
                            size="small"
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                          <span className="quantity-display">{item.qty}</span>
                          <IconButton
                            className="quantity-btn"
                            onClick={() => incrementCartHandler(item)}
                            disabled={item.qty >= item.countInStock}
                            size="small"
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </div>

                        <Tooltip title="Remove from cart" placement="top">
                          <Button
                            className="remove-btn"
                            onClick={() => removeFromCartHandler(item)}
                            startIcon={<DeleteOutline />}
                          >
                            Remove
                          </Button>
                        </Tooltip>
                      </div>
                    </div>

                    <div className="cart-item-price-section">
                      <div className="cart-item-price">
                        {formatCurrency(item.price * item.qty)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="order-summary">
                <h2 className="summary-title">Order Summary</h2>

                <div className="summary-item">
                  <span className="summary-label">Subtotal</span>
                  <span className="summary-value">{formatCurrency(total)}</span>
                </div>

                <div className="summary-item">
                  <span className="summary-label">Shipping</span>
                  <span className="summary-value">FREE</span>
                </div>

                <div className="summary-item">
                  <span className="summary-label">Discount</span>
                  <span className="summary-value">{formatCurrency(0)}</span>
                </div>

                <div className="summary-total">
                  <span className="summary-total-label">Total</span>
                  <span className="summary-total-value">
                    {formatCurrency(total)}
                  </span>
                </div>

                <MinimumOrderNotice
                  shortfall={minimumShortfall}
                  minimum={minimumOrderValue}
                />

                <Button
                  variant="contained"
                  className="checkout-btn"
                  onClick={checkoutHandler}
                  disabled={!products?.length || minimumShortfall > 0}
                >
                  {minimumShortfall > 0
                    ? minimumOrderMessage(minimumShortfall, formatCurrency)
                    : "Proceed to Checkout"}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Image Zoom Modal (Mobile only) */}
        {isMobile && zoomedImage && (
          <div
            className="image-zoom-modal"
            onClick={() => setZoomedImage(null)}
          >
            <div className="image-zoom-modal-close">×</div>
            <div className="image-zoom-modal-content">
              <img src={zoomedImage.url} alt={zoomedImage.name} />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Cart;
